import React, { useCallback, useEffect, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLocation } from "react-router-dom";
import { ChevronDown, Trash2 } from "lucide-react";

import { deleteEvents as onDeleteEvents } from "slices/thunk";
import {
  addEvents,
  getCalendar,
  getCalendarByUser,
  updateEvents,
} from "slices/calendar/thunk";
import DeleteModal from "Common/DeleteModal";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { cn } from "lib/utils";

import "./index.scss";
import SearchFilter from "./searchFilter";
import {
  checkDuplicateStartTime,
  convertDateTimeToFields,
  formatDateLocal,
  generateTimeOptions,
  getNearestTime,
  mapApiEventsToFullCalendar,
  parseDateTime,
} from "./helpers";

const SelectInput: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({
  className,
  children,
  ...props
}) => (
  <div className="relative">
    <select
      className={cn(
        "flex h-10 w-full cursor-pointer appearance-none rounded-md border border-border bg-background px-3 pr-9 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:border-primary/40",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
  </div>
);

const Calendar = () => {
  document.title = `Calendar | EngageHub IQ`;
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const selectDataList = createSelector(
    (state: any) => state.Calendar,
    (state) => ({
      dataList: state.event,
      calendar: state.calendar,
    })
  );
  const { dataList, calendar } = useSelector(selectDataList);
  const timeOptions = generateTimeOptions();

  const [data, setData] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();
  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedDay, setSelectedDay] = useState<any>(0);
  const [searchContactText, setSearchContactText] = useState("");
  const [roleName, setRoleName] = useState("user");
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState(getNearestTime());
  const [endTime, setEndTime] = useState(() => {
    const startIndex = timeOptions.indexOf(startTime);
    return timeOptions[Math.min(startIndex + 1, timeOptions.length - 1)];
  });
  const [isErrorDate, setIsErrorDate] = useState<boolean>(false);
  const [isDuplicateTime, setIsDuplicateTime] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>();
  const [status, setStatus] = useState<any>();
  const [visibility, setVisibility] = useState<any>();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  useEffect(() => {
    setRoleName(localStorage.getItem("role") || "");
  }, []);

  useEffect(() => {
    const uid = query.get("userId");
    if (uid) {
      setUserId(uid);
      dispatch(getCalendarByUser(uid));
    } else {
      dispatch(getCalendar());
    }
  }, [dispatch, query]);

  useEffect(() => {
    if (!dataList) return;
    setData(dataList);
  }, [dataList]);

  useEffect(() => {
    if (calendar?.id) setData(calendar?.events);
  }, [calendar]);

  useEffect(() => {
    if (eventData) {
      const { selectedDate, startTime, endTime } = convertDateTimeToFields(
        eventData.start,
        eventData.end
      );
      setStartTime(startTime);
      setEndTime(endTime);
      setSelectedDate(selectedDate);
    }
  }, [eventData]);

  useEffect(() => {
    if (selectedDay && selectedDay.date) {
      setSelectedDate(selectedDay.date);
      const nearestTime = getNearestTime();
      setStartTime(nearestTime);
      const startIndex = timeOptions.indexOf(nearestTime);
      setEndTime(timeOptions[Math.min(startIndex + 1, timeOptions.length - 1)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  useEffect(() => {
    if (
      (status === "Hidden" || status === "") &&
      (visibility === "Hidden" || visibility === "" || visibility === undefined)
    ) {
      setData(dataList);
    } else if (
      (status !== "Hidden" || status !== "") &&
      (visibility === "Hidden" || visibility === "" || visibility === undefined)
    ) {
      setData(dataList?.filter((item: any) => item?.status === status));
    } else {
      setData(
        dataList?.filter(
          (item: any) =>
            item?.status === status && item?.visibility === visibility
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (
      (visibility === "Hidden" || visibility === "") &&
      (status === "Hidden" || status === "" || status === undefined)
    ) {
      setData(dataList);
    } else if (
      (visibility !== "Hidden" || visibility !== "") &&
      (status === "Hidden" || status === "" || status === undefined)
    ) {
      setData(
        dataList?.filter((item: any) => item?.visibility === visibility)
      );
    } else {
      setData(
        dataList?.filter(
          (item: any) =>
            item?.visibility === visibility && item?.status === status
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  const filterSearchData = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchContactText(e.target.value);
  };
  const keysToSearch = ["title", "guest"];
  const filteredEventData = Array.isArray(data)
    ? mapApiEventsToFullCalendar(
      data.filter((item: any) =>
        keysToSearch.some((key: any) =>
          item[key]?.toLowerCase().includes(searchContactText.toLowerCase())
        )
      )
    )
    : [];

  const handleEventClick = (ele: any) => {
    const event = ele.event;
    setEventData({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end === null ? null : event.end,
      description: event.extendedProps.description,
      location: event.extendedProps.location,
      timeZone: event.extendedProps.timeZone,
      recurrenceFrequency: event.extendedProps.recurrenceFrequency,
      status: event.extendedProps.status,
      visibility: event.extendedProps.visibility,
      isAllDay: event.extendedProps.isAllDay,
      guest: event.extendedProps.guest,
      className: event.classNames.join(" "),
    });
    setIsEdit(true);
    setShow(true);
    setIsErrorDate(false);
    setIsDuplicateTime(false);
  };

  const handleDateClick = (arg: any) => {
    const date = arg["date"];
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const modifiedDate = new Date(year, month, day);
    const modifiedData = { ...arg, date: modifiedDate };
    setSelectedDay(modifiedData);
    toggle();
    setIsErrorDate(false);
    setIsDuplicateTime(false);
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      creator: roleName,
      title: (eventData && eventData?.title) || "",
      description: (eventData && eventData?.description) || "",
      location: (eventData && eventData?.location) || "",
      timeZone: (eventData && eventData?.timeZone) || "",
      recurrenceFrequency:
        (eventData && eventData?.recurrenceFrequency) || "",
      status: (eventData && eventData?.status) || "",
      visibility: (eventData && eventData?.visibility) || "",
      guest: (eventData && eventData?.guest) || "",
      isAllDay: (eventData && eventData?.isAllDay) || false,
      start: isEdit ? eventData?.start || null : selectedDay.date,
      ...(!isEdit || eventData?.end
        ? {
          end: isEdit
            ? eventData?.end || null
            : new Date(selectedDay?.date?.getTime() + 30 * 60000),
        }
        : {}),
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Please Enter title"),
      location: Yup.string().required("Please Enter location"),
      start: Yup.date().nullable().required("Please select start date"),
      end: Yup.date()
        .nullable()
        .when("start", (start, schema) =>
          start ? schema.min(start, "End date must be after start date") : schema
        ),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const { startDateTime, endDateTime } = parseDateTime(
          selectedDate,
          startTime,
          endTime
        );
        const updateData = {
          title: values.title,
          description: values.description,
          location: values.location,
          timeZone: "",
          isAllDay: values.isAllDay,
          recurrenceFrequency: values.recurrenceFrequency,
          status: values.status,
          visibility: values.visibility,
          startTime: startDateTime ? startDateTime : null,
          endTime: endDateTime ? endDateTime : null,
          guest: values.guest,
          organizerId: userId || null,
        };
        if (
          updateData?.startTime &&
          new Date(updateData.startTime) < new Date()
        ) {
          setIsErrorDate(true);
          return;
        }
        const isDuplicate = checkDuplicateStartTime(
          data,
          updateData.startTime,
          updateData.endTime,
          eventData.id
        );
        if (isDuplicate) {
          setIsDuplicateTime(true);
          return;
        }
        dispatch(updateEvents({ id: eventData.id, event: updateData }));
        setIsErrorDate(false);
        setIsDuplicateTime(false);
      } else {
        const { startDateTime, endDateTime } = parseDateTime(
          selectedDate,
          startTime,
          endTime
        );
        const newData = {
          userId: userId || null,
          calendarId: calendar.id,
          organizerId: userId || null,
          title: values.title,
          description: values.description,
          location: values.location,
          timeZone: "",
          isAllDay: values.isAllDay,
          recurrenceFrequency: values.recurrenceFrequency || null,
          status: values.status || null,
          visibility: values.visibility || null,
          startTime: startDateTime || null,
          endTime: endDateTime || null,
          guest: values.guest || null,
        };
        if (newData?.startTime && new Date(newData.startTime) < new Date()) {
          setIsErrorDate(true);
          return;
        }
        const isDuplicate = checkDuplicateStartTime(
          data,
          newData.startTime,
          newData.endTime
        );
        if (isDuplicate) {
          setIsDuplicateTime(true);
          return;
        }
        dispatch(addEvents(newData));
        setIsErrorDate(false);
        setIsDuplicateTime(false);
      }
      toggle();
    },
  });

  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
    } else {
      setShow(true);
      setEventData("");
      validation.resetForm();
      setIsErrorDate(false);
      setIsDuplicateTime(false);
    }
  }, [show, validation]);

  const handleStartTimeChange = (e: any) => {
    setIsErrorDate(false);
    setIsDuplicateTime(false);
    const selected = e.target.value;
    setStartTime(selected);
    const startIndex = timeOptions.indexOf(selected);
    const endIndex = Math.min(startIndex + 1, timeOptions.length - 1);
    setEndTime(timeOptions[endIndex]);
  };

  const handleEndTimeChange = (e: any) => {
    setIsErrorDate(false);
    setIsDuplicateTime(false);
    const selected = e.target.value;
    setEndTime(selected);
    const endIndex = timeOptions.indexOf(selected);
    const startIndex = timeOptions.indexOf(startTime);
    if (endIndex <= startIndex) {
      const i = Math.min(endIndex - 1, timeOptions.length - 1);
      setStartTime(timeOptions[i]);
    }
  };

  const handleDateChange = (e: any) => {
    setIsErrorDate(false);
    setIsDuplicateTime(false);
    setSelectedDate(new Date(e.target.value));
  };

  const handleDelete = () => {
    if (eventData) {
      dispatch(onDeleteEvents(eventData.id));
      setDeleteModal(false);
    }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Calendar Management</CardTitle>
          </div>
          <div className="pt-4">
            <SearchFilter
              textValue={searchContactText}
              onClearSearch={() => setSearchContactText("")}
              onSearch={filterSearchData}
              onStatusFilterChange={(opt: any) => setStatus(opt.value)}
              onVisibilityFilterChange={(opt: any) => setVisibility(opt.value)}
            />
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <div id="calendar">
            <FullCalendar
              plugins={[
                dayGridPlugin,
                multiMonthPlugin,
                interactionPlugin,
                listPlugin,
                timeGridPlugin,
              ]}
              initialView="multiMonthYear"
              themeSystem="tailwindcss"
              slotDuration={"00:30:00"}
              slotMinTime={"00:00:00"}
              slotMaxTime={"20:00:00"}
              handleWindowResize={true}
              weekends={true}
              editable={true}
              droppable={true}
              selectable={true}
              events={filteredEventData}
              timeZone="local"
              headerToolbar={{
                left: "prev,next,today",
                center: "title",
                right:
                  "multiMonthYear,dayGridMonth,dayGridWeek,dayGridDay,listWeek",
              }}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
            />
          </div>
        </CardContent>
      </Card>

      <DeleteModal
        title="Are you sure you want to proceed?"
        show={deleteModal}
        onHide={() => setDeleteModal(!deleteModal)}
        onDelete={handleDelete}
      />

      <Dialog open={show} onOpenChange={(open) => !open && toggle()}>
        <DialogContent className="max-w-lg p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>{isEdit ? "Edit Event" : "Add Event"}</DialogTitle>
          </DialogHeader>

          <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
            <form
              id="form-event"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="event-title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="event-title"
                  name="title"
                  placeholder="Event title"
                  onChange={validation.handleChange}
                  value={validation.values.title || ""}
                />
                {validation.touched.title && validation.errors.title ? (
                  <p className="text-xs font-medium text-destructive">
                    {validation.errors.title}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="event-location">
                  Location <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="event-location"
                  name="location"
                  placeholder="Location"
                  onChange={validation.handleChange}
                  value={validation.values.location || ""}
                />
                {validation.touched.location && validation.errors.location ? (
                  <p className="text-xs font-medium text-destructive">
                    {validation.errors.location}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="event-description">Description</Label>
                <Input
                  id="event-description"
                  name="description"
                  placeholder="Description"
                  onChange={validation.handleChange}
                  value={validation.values.description || ""}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="event-recurrenceFrequency">Recurring</Label>
                  <SelectInput
                    id="event-recurrenceFrequency"
                    name="recurrenceFrequency"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.recurrenceFrequency}
                  >
                    <option value="">Select frequency</option>
                    <option value="None">Does not repeat</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </SelectInput>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="event-status">Status</Label>
                  <SelectInput
                    id="event-status"
                    name="status"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.status}
                  >
                    <option value="">Select status</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Tentative">Tentative</option>
                    <option value="Cancelled">Cancelled</option>
                  </SelectInput>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="event-visibility">Visibility</Label>
                  <SelectInput
                    id="event-visibility"
                    name="visibility"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.visibility}
                  >
                    <option value="">Select visibility</option>
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                    <option value="Default">Default</option>
                  </SelectInput>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="event-guest">Guest</Label>
                  <Input
                    id="event-guest"
                    name="guest"
                    placeholder="Enter guest email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.guest || ""}
                  />
                </div>
              </div>

              {/* Date + time */}
              <div className="space-y-1.5">
                <Label>Date & time</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="date"
                    name="start"
                    onChange={handleDateChange}
                    value={selectedDate ? formatDateLocal(selectedDate) : ""}
                    className="w-auto flex-1 min-w-[140px]"
                  />
                  <SelectInput
                    value={startTime}
                    onChange={handleStartTimeChange}
                    className="w-auto min-w-[110px]"
                  >
                    {timeOptions.map((time, index) => (
                      <option key={index} value={time}>
                        {time}
                      </option>
                    ))}
                  </SelectInput>
                  <span className="text-muted-foreground">—</span>
                  <SelectInput
                    value={endTime}
                    onChange={handleEndTimeChange}
                    className="w-auto min-w-[110px]"
                  >
                    {timeOptions.map((time, index) => (
                      <option key={index} value={time}>
                        {time}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                {isErrorDate && (
                  <p className="text-xs font-medium text-destructive">
                    Start time cannot be in the past
                  </p>
                )}
                {isDuplicateTime && (
                  <p className="text-xs font-medium text-destructive">
                    The selected time slot is not available
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="event-creator">Creator</Label>
                  <Input
                    id="event-creator"
                    name="creator"
                    disabled
                    value={validation.values.creator}
                  />
                </div>
                <label
                  htmlFor="event-isAllDay"
                  className="mt-7 inline-flex cursor-pointer items-center gap-2 text-sm text-foreground"
                >
                  <input
                    id="event-isAllDay"
                    type="checkbox"
                    name="isAllDay"
                    onChange={(e) =>
                      validation.setFieldValue("isAllDay", e.target.checked)
                    }
                    checked={validation.values.isAllDay}
                    className="h-4 w-4 rounded border border-border bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-1"
                  />
                  All day event
                </label>
              </div>
            </form>
          </div>

          <DialogFooter className="border-t border-border/60 px-6 py-4">
            <Button variant="outline" onClick={toggle}>
              Cancel
            </Button>
            {isEdit && (
              <Button
                variant="destructive"
                onClick={() => {
                  setShow(false);
                  setDeleteModal(true);
                  if (eventData?.id) setEventData(eventData);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
            <Button type="submit" form="form-event">
              {isEdit ? "Update" : "Add event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Calendar;
